import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Search } from "lucide-react";
import faqData from "../../data/faqData";

export default function FAQAccordion({
  selectedCategory = "get-started",
  searchQuery = "",
}) {
  const [openIndex, setOpenIndex] = useState(null);

  // Filter FAQs based on search query
  const getFilteredFaqs = () => {
    const categoryFaqs = faqData[selectedCategory] || faqData["get-started"];

    if (!searchQuery.trim()) {
      return categoryFaqs;
    }

    const query = searchQuery.toLowerCase();
    return categoryFaqs.filter(
      (faq) =>
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query)
    );
  };

  const currentFaqs = getFilteredFaqs();

  // Highlight search terms in text
  const highlightText = (text, query) => {
    if (!query.trim()) return text;

    const regex = new RegExp(
      `(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    return text.split(regex).map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 text-gray-800 px-1 rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  if (currentFaqs.length === 0 && searchQuery.trim()) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-6 text-center py-12"
      >
        <div className="text-gray-500 mb-2">
          <Search size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            No results found
          </h3>
          <p className="text-sm text-gray-500">
            We couldn't find any FAQs matching "{searchQuery}". Try different
            keywords or browse our categories.
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div layout className="mt-6 space-y-3">
      {currentFaqs.map((faq, idx) => {
        const isOpen = openIndex === idx;
        return (
          <motion.div
            key={faq.question}
            layout
            className={`rounded-xl border transition-all duration-200 ${
              isOpen
                ? "bg-white border-emerald-200 shadow-md"
                : "bg-white/80 border-emerald-100 hover:border-emerald-200 hover:shadow-sm"
            }`}
          >
            <button
              className="flex gap-4 justify-between items-center px-4 py-4 w-full text-sm font-medium text-left text-gray-800 hover:text-emerald-700 transition-colors"
              onClick={() => setOpenIndex(isOpen ? null : idx)}
              aria-expanded={isOpen}
            >
              <span className="flex-1 pr-2">
                {highlightText(faq.question, searchQuery)}
              </span>
              <ChevronDown
                size={18}
                className={`transition-all duration-200 text-emerald-600 ${
                  isOpen ? "rotate-180" : "rotate-0"
                }`}
              />
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 0.61, 0.36, 1] }}
                  style={{ overflow: "hidden" }}
                >
                  <div className="px-4 pb-4 border-t border-emerald-50">
                    <div className="pt-3 text-sm text-gray-600 leading-relaxed">
                      {faq.answer.split("\n").map((line, index) => {
                        if (line.startsWith("•")) {
                          return (
                            <div
                              key={index}
                              className="flex items-start gap-3 mt-3 first:mt-0"
                            >
                              <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 flex-shrink-0"></div>
                              <span className="text-gray-600">
                                {highlightText(
                                  line.substring(1).trim(),
                                  searchQuery
                                )}
                              </span>
                            </div>
                          );
                        }
                        return (
                          <div
                            key={index}
                            className={index === 0 ? "" : "mt-3"}
                          >
                            {highlightText(line, searchQuery)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </motion.div>
  );
}
